/* eslint-disable jsx-a11y/anchor-is-valid, no-script-url */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import Promise from 'bluebird';
import * as urlUtil from '@bingads-webui/url-util';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ClientCenterCustomerService } from '@bingads-webui/clientcenter-customer-service';
import { oldAlertCenterAdapter } from '@bingads-webui-campaign/notification-center';
import asyncScheduler from '@bingads-webui-static-legacy/async-scheduler';
import * as errI18n from '@bingads-webui-component-legacy/api-errors-campaign/index';

import {
  RecommendationService,
  RecommendationDownloadService,
  setRecommendationCache,
  getRecommendationCache,
  MCC_ACCOUNTS_CAPPING,
} from '@bingads-webui-campaign/recommendation-core';

const flatten = entities => _.chain(entities)
  .map((entity) => {
    if (entity.isCustomer) {
      if (entity.unAuthorized) {
        return [];
      }
      return flatten(entity.children);
    }
    return entity;
  })
  .flatten()
  .value();

export const withRecommendationServices = ({
  currentScenario,
  currentActivity,
  odataPath,
}) => (WrappedComponent) => {
  const HoC = (props) => {
    const {
      appConfig,
      permissions,
      pageContext,
      i18n,
      odata,
      cacheManager,
    } = props.deps;

    const { getODataErrors } = errI18n;

    const customerService = new ClientCenterCustomerService({ ccBaseUrl: appConfig.get('CCBaseUrl') });
    let accountIdsPromise = null;
    if (props.isMCC) {
      const AccountIdListCache = getRecommendationCache('account-id-list');
      accountIdsPromise = _.isUndefined(AccountIdListCache) ? customerService.getCustomerHierarchy({
        cid: props.scope.customerId,
        uid: props.scope.userId,
        returnOnlyDirectlyLinkedEntities: false,
        limit: MCC_ACCOUNTS_CAPPING,
      }).then((data) => {
        const { customerHierarchyEntities } = data;
        const flattenedAccounts = flatten(_.first(customerHierarchyEntities).children);
        const result = _.chain(flattenedAccounts).map(account => account.id).value();
        setRecommendationCache('account-id-list', result);
        return result;
      }) : Promise.resolve(AccountIdListCache);
    } else {
      accountIdsPromise = Promise.resolve([]);
    }

    const source = urlUtil.getParameter('recsrc') || appConfig.get('recsrc');

    // don't initialize if scope is not set
    let dataService = null;
    let downloadService = null;
    if (props.scope != null) {
      dataService = new RecommendationService({
        scope: props.scope,
        showAlertHandler: oldAlertCenterAdapter.show.bind(oldAlertCenterAdapter),
        i18n,
        currentScenario,
        currentActivity,
        odataPath,
        permissions,
        lcid: pageContext.CultureLcid,
        pageContext,
        ccJsData: props.ccJsData,
        accountIdsPromise,
        isMCC: props.isMCC,
        asyncScheduler,
        alertCenter: oldAlertCenterAdapter,
        odata,
        newI18n: props.vnextDeps.newI18n || props.newI18n,
        cacheManager,
        channel: props.channel,
      });
      downloadService = new RecommendationDownloadService({
        scope: props.scope,
        alertCenter: oldAlertCenterAdapter,
        asyncScheduler,
        cacheManager,
        getODataErrors,
        i18n,
        odata,
        pageContext,
        permissions,
        isMCC: props.isMCC,
        recommendationService: dataService,
      });
    }

    return (
      <WrappedComponent
        {...props}
        dataService={dataService}
        downloadService={downloadService}
        currentActivity={currentActivity}
        source={source}
      />
    );
  };

  HoC.displayName = `withService(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    deps: PropTypes.shape({
      appConfig: PropTypes.objectOf(PropTypes.any).isRequired,
      instrumentation: PropTypes.object,
      permissions: PropTypes.object.isRequired,
      pageContext: PropTypes.object.isRequired,
      i18n: PropTypes.object.isRequired,
    }).isRequired,
    scope: PropTypes.shape({
      accountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      adGroupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      token: PropTypes.string,
    }),
    ccJsData: PropTypes.objectOf(PropTypes.object),
    isMCC: PropTypes.bool,
    vnextDeps: PropTypes.shape({
      newI18n: PropTypes.object,
    }),
    channel: PropTypes.string,
  };
  HoC.defaultProps = {
    scope: null,
    isMCC: false,
    ccJsData: undefined,
    vnextDeps: {},
    channel: null,
  };

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};
